import { FileText, Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Card } from '@/components/ui/card'
import { useElapsedLabel } from '@/hooks/use-elapsed-label'

export interface SchemaAgentProgressFile {
  filename: string
  missing: boolean
}

export interface SchemaAgentProgressProps {
  isConnected: boolean
  startedAt: string | null
  /** Null until the digests are ready; empty for a session without reference files. */
  files: SchemaAgentProgressFile[] | null
  stepsCompleted: number
  lastTool: string | null
}

/**
 * Live panel while a schema session runs: the phase (digesting the files,
 * then composing), the elapsed time, the digested files and the agent's last
 * tool call — mirroring the OSINT progress panel so the two feel identical.
 */
export function SchemaAgentProgress({
  isConnected,
  startedAt,
  files,
  stepsCompleted,
  lastTool,
}: SchemaAgentProgressProps) {
  const { t } = useTranslation()
  const elapsed = useElapsedLabel(startedAt)
  const phase = !isConnected
    ? t('schemaAgent.progress.reconnecting')
    : files === null
      ? t('schemaAgent.progress.digesting')
      : t('schemaAgent.progress.composing')
  const toolLabel = lastTool ? t(`schemaAgent.progress.tool.${lastTool}`, lastTool) : null

  return (
    <Card tone="primary" className="flex flex-col gap-4 p-5">
      <div className="flex items-center gap-3">
        <Loader2 className="size-4 shrink-0 animate-spin text-primary" />
        <div className="flex flex-1 items-center justify-between gap-3 text-xs">
          <span className="font-medium text-foreground">{phase}</span>
          {elapsed && (
            <span className="font-mono tabular-nums text-muted-foreground">{elapsed}</span>
          )}
        </div>
      </div>

      {!!files?.length && (
        <ul className="flex flex-col gap-1">
          {files.map(file => (
            <li
              key={`${file.filename}-${file.missing}`}
              className="flex items-center gap-2 text-xs text-muted-foreground"
            >
              <FileText className="size-3 shrink-0" />
              <span className={file.missing ? 'line-through' : undefined}>{file.filename}</span>
            </li>
          ))}
        </ul>
      )}

      {toolLabel && (
        <p className="text-xs text-muted-foreground">
          {toolLabel}
          {stepsCompleted > 0 && (
            <span className="ml-1 tabular-nums">
              · {t('schemaAgent.progress.steps', { count: stepsCompleted })}
            </span>
          )}
        </p>
      )}
    </Card>
  )
}
